import React, { useState } from "react";

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    consent: true,
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Form Submission",
          sender_name: formData.fullName,
          sender_email: formData.email,
          consent: formData.consent ? "Accepted" : "Not Accepted",
          message: formData.message,
          phone: formData.phone,
          reply_to: formData.email,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert("Message sent successfully!");
        setFormData({ fullName: "", email: "", consent: true, phone: "", message: "" });
      } else {
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="w-full py-16 md:py-20 lg:py-24 bg-[#EFFFFD]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C3E50] mb-4 font-['Inter']">
            Contact <span className="text-[#21A6B5]">Us</span>
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto font-['Inter']">
            We're here to help! Whether you have questions about your loan or need assistance.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* LEFT SIDE – Contact Info */}
          <div className="flex flex-col gap-6">
            {/* Info Card */}
            <div className="bg-white border-2 border-gray-200 rounded-3xl p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-[#2C3E50] mb-6 font-['Inter']">Get In Touch</h3>
              <div className="space-y-6">
                {[
                  { label: "Phone", value: import.meta.env.VITE_COMPANY_PHONE, href: `tel:${import.meta.env.VITE_COMPANY_PHONE}`, icon: "📞" },
                  { label: "Email", value: import.meta.env.VITE_COMPANY_EMAIL, href: `mailto:${import.meta.env.VITE_COMPANY_EMAIL}`, icon: "📧" },
                  { label: "Address", value: import.meta.env.VITE_COMPANY_ADDRESS, href: undefined, icon: "📍" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1 font-['Inter']">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-[#2C3E50] font-semibold hover:text-[#21A6B5] transition-colors font-['Inter']">{item.value}</a>
                      ) : (
                        <span className="text-[#2C3E50] font-semibold font-['Inter'] text-sm">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-3xl overflow-hidden border-2 border-gray-200 shadow-sm">
              <iframe
                title="MySalaryLoan Location"
                src={import.meta.env.VITE_COMPANY_ADDRESS_MAP_URL}
                width="100%"
                height="300"
                className="border-0 block"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>

          {/* RIGHT SIDE – Contact Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-white border-2 border-gray-200 rounded-3xl p-8 shadow-sm flex flex-col gap-5"
          >
            <h3 className="text-2xl font-bold text-[#2C3E50] mb-2 font-['Inter']">Send us a Message</h3>
            <p className="text-gray-600 mb-4 font-['Inter']">Fill out the form below and we'll get back to you shortly.</p>

            <input 
              type="text" 
              name="fullName" 
              placeholder="Your Name *" 
              value={formData.fullName} 
              onChange={handleChange} 
              required 
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#21A6B5] focus:outline-none transition-colors font-['Inter']" 
            />
            <input 
              type="email" 
              name="email" 
              placeholder="Email Address *" 
              value={formData.email} 
              onChange={handleChange} 
              required 
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#21A6B5] focus:outline-none transition-colors font-['Inter']" 
            />
            <input 
              type="tel" 
              name="phone" 
              placeholder="Phone Number *" 
              value={formData.phone} 
              onChange={handleChange} 
              required 
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#21A6B5] focus:outline-none transition-colors font-['Inter']" 
            />
            <textarea 
              name="message" 
              placeholder="Your Message" 
              value={formData.message} 
              onChange={handleChange} 
              rows={4} 
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#21A6B5] focus:outline-none transition-colors font-['Inter']" 
            />

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="consent"
                checked={formData.consent}
                onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                className="mt-1 h-5 w-5 accent-[#21A6B5] cursor-pointer"
              />
              <label className="text-sm text-gray-600 font-['Inter']">
                I consent to receive updates via WhatsApp, email, or SMS.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 mt-2 rounded-full font-semibold text-white bg-[#21A6B5] hover:bg-[#1B8E9B] transition-colors disabled:opacity-60 font-['Inter']"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
